import { useState, useMemo } from 'react';
import { useList, useCustomMutation, useApiUrl } from '@refinedev/core';
import { Check, Loader2, Tag, X } from 'lucide-react';
import { notify } from '@/lib/notify';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';

interface TagOption {
  id: number;
  name: string;
  color: string;
  deletedAt: string | null;
}

interface BulkTagsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** ID пользователей, которым назначаем/снимаем теги */
  userIds: number[];
  onSuccess?: () => void;
  /** Человекочитаемое описание выбранных элементов */
  selectionLabel?: string;
}

export function BulkTagsDialog({
  open,
  onOpenChange,
  userIds,
  onSuccess,
  selectionLabel,
}: BulkTagsDialogProps) {
  const [selectedTagIds, setSelectedTagIds] = useState<Set<number>>(new Set());
  const [mode, setMode] = useState<'assign' | 'remove'>('assign');

  const tagsResult = useList<TagOption>({
    resource: 'tags',
    pagination: { mode: 'off' },
    queryOptions: { staleTime: 30_000, enabled: open },
  });

  const activeTags = useMemo(
    () => (tagsResult.result.data ?? []).filter((t) => t.deletedAt === null),
    [tagsResult.result.data],
  );

  const apiUrl = useApiUrl();
  const bulkMutation = useCustomMutation();
  const isPending = bulkMutation.mutation.isPending;

  const toggleTag = (id: number) => {
    setSelectedTagIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleClose = () => {
    if (isPending) return;
    setSelectedTagIds(new Set());
    setMode('assign');
    onOpenChange(false);
  };

  const handleSubmit = () => {
    if (selectedTagIds.size === 0) {
      notify.error('Выберите хотя бы один тег');
      return;
    }

    bulkMutation.mutate(
      {
        url: `${apiUrl}/tags/users/bulk-assign`,
        method: 'post',
        values: {
          userIds,
          tagIds: Array.from(selectedTagIds),
          mode,
        },
      },
      {
        onSuccess: () => {
          const action = mode === 'assign' ? 'назначен' : 'снят';
          notify.success(
            `${selectedTagIds.size === 1 ? 'Тег' : 'Теги'} ${action} для ${userIds.length} ${pluralizeUsers(userIds.length)}`,
          );
          handleClose();
          onSuccess?.();
        },
        onError: (error) => {
          notify.error(error.message ?? 'Ошибка при изменении тегов');
        },
      },
    );
  };

  const label = selectionLabel ?? `${userIds.length} ${pluralizeUsers(userIds.length)}`;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[460px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Tag className="h-5 w-5" />
            Управление тегами
          </DialogTitle>
          <DialogDescription>
            {mode === 'assign' ? 'Назначить теги' : 'Снять теги'} для: <strong>{label}</strong>
          </DialogDescription>
        </DialogHeader>

        {/* Переключатель режима */}
        <div className="flex gap-2">
          <Button
            type="button"
            size="sm"
            variant={mode === 'assign' ? 'default' : 'outline'}
            onClick={() => setMode('assign')}
            disabled={isPending}
          >
            <Check className="h-3.5 w-3.5 mr-1.5" />
            Назначить
          </Button>
          <Button
            type="button"
            size="sm"
            variant={mode === 'remove' ? 'destructive' : 'outline'}
            onClick={() => setMode('remove')}
            disabled={isPending}
          >
            <X className="h-3.5 w-3.5 mr-1.5" />
            Снять
          </Button>
        </div>

        <Separator />

        {/* Выбранные теги */}
        {selectedTagIds.size > 0 && (
          <div className="flex flex-wrap gap-1.5 min-h-[32px]">
            {[...selectedTagIds].map((id) => {
              const tag = activeTags.find((t) => t.id === id);
              if (!tag) return null;
              return (
                <Badge
                  key={id}
                  variant="secondary"
                  className="gap-1 cursor-pointer hover:opacity-80 pr-1"
                  style={{ borderLeftColor: tag.color, borderLeftWidth: 3 }}
                  onClick={() => toggleTag(id)}
                >
                  <span
                    className="h-2 w-2 rounded-full shrink-0"
                    style={{ backgroundColor: tag.color }}
                  />
                  {tag.name}
                  <X className="h-3 w-3 opacity-60" />
                </Badge>
              );
            })}
          </div>
        )}

        {/* Список тегов */}
        <Command className="border rounded-md">
          <CommandInput placeholder="Поиск тегов..." disabled={isPending} />
          <CommandList className="max-h-[240px]">
            {tagsResult.query.isLoading ? (
              <div className="flex items-center justify-center py-6 text-muted-foreground text-sm">
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Загрузка...
              </div>
            ) : (
              <>
                <CommandEmpty>Теги не найдены</CommandEmpty>
                <CommandGroup>
                  {activeTags.map((tag) => {
                    const isSelected = selectedTagIds.has(tag.id);
                    return (
                      <CommandItem
                        key={tag.id}
                        value={`${tag.name}-${tag.id}`}
                        onSelect={() => toggleTag(tag.id)}
                        className="gap-2"
                      >
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={() => toggleTag(tag.id)}
                          className="pointer-events-none"
                        />
                        <span
                          className="h-3 w-3 rounded-full shrink-0"
                          style={{ backgroundColor: tag.color }}
                        />
                        <span className={cn('flex-1', isSelected && 'font-medium')}>
                          {tag.name}
                        </span>
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={handleClose} disabled={isPending}>
            Отмена
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={isPending || selectedTagIds.size === 0}
            variant={mode === 'remove' ? 'destructive' : 'default'}
          >
            {isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            {mode === 'assign'
              ? `Назначить${selectedTagIds.size > 0 ? ` (${selectedTagIds.size})` : ''}`
              : `Снять${selectedTagIds.size > 0 ? ` (${selectedTagIds.size})` : ''}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function pluralizeUsers(count: number): string {
  if (count % 10 === 1 && count % 100 !== 11) return 'пользователя';
  if (count % 10 >= 2 && count % 10 <= 4 && (count % 100 < 10 || count % 100 >= 20)) {
    return 'пользователям';
  }
  return 'пользователям';
}
